import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { BASE_URL } from "./Constants";
import useAuth from "./useAuth";
import * as XLSX from "xlsx";

const Reports = () => {
  useAuth();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchReports = async () => {
      const token = localStorage.getItem("authToken");
      try {
        const response = await fetch(`${BASE_URL}reports1`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch report data");

        const res = await response.json();
        setData(res.data);
        setFilteredData(res.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  useEffect(() => {
    let filtered = data;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();

      filtered = data.filter(item =>
        (item.emp_id ?? "").toLowerCase().includes(q) ||
        (item.comp_name ?? "").toLowerCase().includes(q) ||
        (item.dept_name ?? "").toLowerCase().includes(q) ||
        (item.location_name ?? "").toLowerCase().includes(q) ||
        (item.question ?? "").toLowerCase().includes(q) ||
        (item.emp_answer ?? "").toLowerCase().includes(q) ||
        (item.correct_answer ?? "").toLowerCase().includes(q) ||
        (item.score ?? "").toString().includes(q)
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchQuery, data]);


  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const exportToExcel = () => {
    const formattedData = filteredData.map((item, index) => ({
      "S.No": index + 1,
      "Employee ID": item.emp_id,
      "Employee Name": item.comp_name,
      "Department": item.dept_name,
      "Location": item.location_name,
      "Question": item.question,
      "Written Answer": item.emp_answer,
      "Correct Answer": item.correct_answer,
      "Marks": item.score,
      "Submitted_Date": item.submitted_date,
    }));

    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employee Written Marks");
    XLSX.writeFile(wb, "employee_written_marks.xlsx");
  };


  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />

        <div className="main-panel">
          <div className="content-wrapper">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title">Employee Written Marks</h4>

                <div className="row mb-3">
                  <div className="col-md-1">
                    <select
                      className="form-control input-font"
                      style={{ padding: "0px 10px" }}
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    >
                      <option>10</option>
                      <option>20</option>
                      <option>50</option>
                      <option>100</option>
                    </select>
                  </div>

                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control input-font"
                      placeholder="Search by Employee ID, Question, Answer..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <button
                      className="btn btn-success input-font"
                      style={{ padding: "5px 10px" }}
                      onClick={exportToExcel}
                    >
                      Export to Excel
                    </button>
                  </div>
                </div>

                {loading && <p>Loading...</p>}
                {error && <p className="text-danger">{error}</p>}

                {!loading && filteredData.length > 0 && (
                  <>
                    <div style={{ overflowX: "auto", width: "100%" }}>
                      <table className="table table-bordered" style={{ minWidth: "1400px" }}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Employee ID</th>
                            <th>Employee Name</th>
                            <th>Question</th>
                            <th>Written Answer</th>
                            <th>Correct Answer</th>
                            <th>Marks</th>
                            <th>Department</th>
                            <th>Location</th>
                          </tr>
                        </thead>

                        <tbody>
                          {paginatedData.map((item, index) => (
                            <tr key={index}>
                              <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td>{item.emp_id}</td>
                              <td>{item.comp_name}</td>

                              <td>{item.question}</td>
                              <td>{item.emp_answer}</td>
                              <td style={{
                                color: item.emp_answer === item.correct_answer ? "green" : "red"
                              }}>
                                {item.emp_answer}
                              </td>
                              <td
                                style={{
                                  color: Number(item.score) === 0 ? "red" : "green",
                                  fontWeight: "bold",
                                }}
                              >
                                {item.score}
                              </td>
                              <td>{item.dept_name}</td>
                              <td>{item.location_name}</td>
                            </tr>
                          ))}
                        </tbody>

                      </table>
                    </div>
                    <br />
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <button
                        className="btn btn-secondary"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                      >
                        Previous
                      </button>

                      <span>Page {currentPage}</span>

                      <button
                        className="btn btn-secondary"
                        disabled={
                          currentPage >=
                          Math.ceil(filteredData.length / itemsPerPage)
                        }
                        onClick={() => setCurrentPage((p) => p + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}

                {!loading && filteredData.length === 0 && (
                  <p>No records found</p>
                )}
              </div>
            </div>
          </div>

          <footer className="footer">
            <span className="text-muted">
              © {new Date().getFullYear()} Vijaya Diagnostic Centre
            </span>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default Reports;
